/**
 * The notification layer: toasts and the "database is blocked" banner, floating over the viewport.
 *
 * These used to be rendered by the toolbar, inside the toolbar's own flex row. That made the
 * toolbar the notification area, and because its grid row was `minmax(32px, auto)` a long enough
 * message wrapped it onto a second line and pushed the whole layout down (audit A3). Messages are
 * transient; the layout is not. So they moved here, into a layer that has zero layout footprint:
 * absolutely positioned, `pointer-events: none`, inset over `containers.viewport`.
 *
 * Zero footprint is a hard requirement, not a preference: the viewport sizes its canvas from
 * `container.clientHeight`, so anything in this container that took flow space would shrink the
 * 3D view by exactly its own height.
 */

import './toolbar.css';

import type { EditorContext } from '../context';
import type { EditorState } from '../index';
import { el } from './dom';
import { createPanel, type PanelHandle } from './panel';

/** How long an info toast stays up. Errors persist until clicked (SPEC "Error Handling"). */
const TOAST_MS = 4000;

export function mountNotifications(container: HTMLElement, ctx: EditorContext): PanelHandle {
  const { store } = ctx;
  let toastTimer: ReturnType<typeof setTimeout> | null = null;

  function render(root: HTMLElement): void {
    const state = store.getState();
    const layer = el('div', { class: 'toasts' });

    /*
     * The blocked-database banner is not a toast: it is a condition, so it has no timer and no
     * dismiss. It sits above the transient toast because it outlives it.
     */
    if (state.ui.dbBlocked) {
      layer.appendChild(
        el('span', {
          class: 'toast error',
          'data-state': 'db-blocked',
          text: 'Motion Kit is open in another tab — close it, then reload.',
          title: 'The local database cannot be upgraded while another tab holds it open.',
        }),
      );
    }

    const toast = state.ui.toast;
    if (toast) {
      layer.appendChild(
        el(
          'span',
          {
            class: `toast ${toast.kind}`,
            'data-toast': String(toast.id),
            title: 'Click to dismiss',
            onclick: () => store.actions.dismissToast(),
          },
          [toast.message],
        ),
      );
    }

    root.appendChild(layer);
  }

  const instance = createPanel(container, ctx, render, {
    className: 'notifications',
    slices: [(s) => s.ui.toast, (s) => s.ui.dbBlocked],
  });

  // Auto-dismiss info toasts. Done on a timer (never inside the store listener) and guarded by
  // the toast id so a newer toast is not dismissed by an older timer.
  function armToast(toast: EditorState['ui']['toast']): void {
    if (toastTimer !== null) clearTimeout(toastTimer);
    toastTimer = null;
    if (!toast || toast.kind === 'error') return;
    const id = toast.id;
    toastTimer = setTimeout(() => {
      toastTimer = null;
      if (store.getState().ui.toast?.id === id) store.actions.dismissToast();
    }, TOAST_MS);
  }

  armToast(store.getState().ui.toast);
  instance.own(store.select((s) => s.ui.toast, armToast));
  instance.own(() => {
    if (toastTimer !== null) clearTimeout(toastTimer);
  });

  return instance;
}
